import React, { useState, useEffect } from 'react';
import { Autocomplete, Button, TextField, Stack, Typography, MenuItem } from '@mui/material';
import { DateRangePicker } from 'rsuite';
import CustomModal from '../../../../common-utilities/core/modal';
import { CIRCLE_OPTIONS, REGION_OPTIONS } from '../../cf-v2-create-form/config';
import { useSelector } from 'react-redux';
import { invokeApi, HTTP_METHODS } from '../../../utils/http-service';
import { INTRANETHOSTNAME } from '../../../../common-utilities/utils/end-points';
import { REST_URLS as INTRANET_URLS } from "../../../../common-utilities/utils/end-points/intranet";
import { CF_STATUS_OPTIONS, TERMINAL_STATUSES } from '../v2-config';



const CfV2FilterModal = ({ onClose, onApply, initialFilters = {} }) => {
  // Step Master comes from redux (loaded once on app open).
  const allStepsMaster = useSelector((state) => state.cfApprovalV2.stepMaster) || [];
  const [userSearch, setUserSearch] = useState('');
  const [userMap, setUserMap] = useState({})
  const [localFilters, setLocalFilters] = useState({
    dateFrom: initialFilters.dateFrom || null,
    dateTo: initialFilters.dateTo || null,
    siteName: initialFilters.siteName || '',
    id: initialFilters.id || '',
    region: initialFilters.region || '',
    circle: initialFilters.circle || '',
    createdBy: initialFilters.createdBy || '',
    currentStep: initialFilters.currentStep || '',
    cfStatus: initialFilters.cfStatus || '',
  });

  useEffect(() => {
    loadUsers()
  }, []);

  const loadUsers = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${INTRANET_URLS.FILTER_USER}`,
      null,
      { appLink: "cf-approval-v2", roles: "BD_LEAD", page: 1, limit: 100 },
    ).then((res) => {
      if (Array.isArray(res?.results)) {
        const map = {};
        res.results.forEach((user) => {
          map[user.id] = user.name;
        });
        setUserMap(map);
      }
    });
  }

  const handleApply = () => {
    const applied = {};
    if (localFilters.dateFrom) applied.dateFrom = localFilters.dateFrom;
    if (localFilters.dateTo) applied.dateTo = localFilters.dateTo;
    if (localFilters.siteName?.trim()) applied.siteName = localFilters.siteName.trim();
    if (localFilters.id) applied.id = Number(localFilters.id);
    if (localFilters.region) applied.region = localFilters.region;
    if (localFilters.circle?.trim()) applied.circle = localFilters.circle.trim();
    if (localFilters.createdBy) applied.createdBy = Number(localFilters.createdBy);

    if (localFilters.cfStatus) {
      applied.cfStatus = localFilters.cfStatus;
    } else if (localFilters.currentStep) {
      applied.currentStep = localFilters.currentStep;
    }

    onApply(applied);
  };

  const handleClear = () => {
    setLocalFilters({
      dateFrom: null,
      dateTo: null,
      siteName: '',
      id: '',
      region: '',
      circle: '',
      createdBy: '',
      currentStep: '',
      cfStatus: '',
    });
  };

  return (
    <CustomModal
      title="Filters"
      onClose={onClose}
      className="cf-v2-filter-modal"
      contentClassName={{ customClass: 'cf-filter-modal' }}
      disabledOutsideClick
    >
      <Stack spacing={2.5} sx={{ p: 2 }}>
        <div>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Created At (Date Range)
          </Typography>
          <DateRangePicker
            cleanable
            placeholder="Select Date Range"
            onChange={(val) => {
              if (!val?.[0]) {
                setLocalFilters((prev) => ({ ...prev, dateFrom: null, dateTo: null }));
                return;
              }
              setLocalFilters((prev) => ({
                ...prev,
                dateFrom: new Date(val[0]).toISOString(),
                dateTo: new Date(val[1]).toISOString(),
              }));
            }}
            value={
              localFilters.dateFrom && localFilters.dateTo
                ? [new Date(localFilters.dateFrom), new Date(localFilters.dateTo)]
                : null
            }
            style={{ width: '100%' }}
          />
        </div>
        <TextField
          label="Site Name"
          size="small"
          value={localFilters.siteName}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, siteName: e.target.value }))}
          fullWidth
          placeholder="Search by site name"
        />
        <TextField
          label="CF ID"
          size="small"
          type="number"
          value={localFilters.id}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, id: e.target.value }))}
          fullWidth
          placeholder="Enter CF ID"
          inputProps={{ min: 1 }}
        />
        <TextField
          select
          label="Created By"
          size="small"
          value={localFilters.createdBy}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, createdBy: e.target.value }))}
          fullWidth
          SelectProps={{
            onClose: () => setUserSearch(''),
          }}
        >
          <MenuItem
            disableRipple
            onClickCapture={(e) => e.stopPropagation()}
            sx={{ p: 0, '&:hover': { background: 'none' } }}
          >
            <TextField
              size="small"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              fullWidth
              sx={{ px: 1, py: 0.5 }}
              autoFocus
            />
          </MenuItem>
          <MenuItem value="">All Users</MenuItem>
          {Object.entries(userMap)
            .filter(([, name]) =>
              name.toLowerCase().includes(userSearch.toLowerCase())
            )
            .map(([id, name]) => (
              <MenuItem key={id} value={id}>{name}</MenuItem>
            ))}
        </TextField>
        <TextField
          select
          label="Region"
          size="small"
          value={localFilters.region}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, region: e.target.value }))}
          fullWidth
        >
          <MenuItem value="">All Regions</MenuItem>
          {REGION_OPTIONS.map((r) => (
            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Circle"
          size="small"
          value={localFilters.circle}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, circle: e.target.value }))}
          fullWidth
        >
          <MenuItem value="">All Circles</MenuItem>
          {CIRCLE_OPTIONS.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>
        <Autocomplete
          options={allStepsMaster.map((s) => ({
            label: `Step ${s.stepNumber} - ${s.name}`,
            value: s.stepNumber,
          }))}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={
            allStepsMaster
              .filter((s) => s.stepNumber === localFilters.currentStep)
              .map((s) => ({ label: `Step ${s.stepNumber} - ${s.name}`, value: s.stepNumber }))[0] ||
            null
          }
          onChange={(e, selected) =>
            setLocalFilters((prev) => ({
              ...prev,
              currentStep: selected ? selected.value : '',
              cfStatus: '',
            }))
          }
          size="small"
          fullWidth
          renderInput={(params) => (
            <TextField {...params} label="Current Step" size="small" placeholder="All Steps" />
          )}
        />
        <TextField
          select
          label="CF Status"
          size="small"
          value={localFilters.cfStatus}
          onChange={(e) =>
            setLocalFilters((prev) => ({ ...prev, cfStatus: e.target.value, currentStep: '' }))
          }
          fullWidth
        >
          <MenuItem value="">All</MenuItem>
          {[
            ...CF_STATUS_OPTIONS,
            ...TERMINAL_STATUSES.map((s) => ({ label: s, value: s })),
          ].map((s) => (
            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
          <Button variant="outlined" size="small" onClick={handleClear}>
            Clear All
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            sx={{ backgroundColor: '#008938', '&:hover': { backgroundColor: '#006b2d' } }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </CustomModal>
  );
};

export default CfV2FilterModal;
